import React, { useEffect, useState } from "react";
import "./Dashboard.css";
import ReactApexChart from "react-apexcharts";

import GroupIcon from "@mui/icons-material/Group";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Category, Reorder } from "@mui/icons-material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PlaceIcon from "@mui/icons-material/Place";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { dashboard } from "../../service/Mainservice";
import Loader from "../../Loader/Loader";
import { useNavigate } from "react-router";

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);

  // ---------- Chart States ----------
  // Combined Registrations (Customer, Vendor, Driver)
  const [registrationsChart, setRegistrationsChart] = useState({
    series: [
      { name: "Customers", data: [] },
      { name: "Vendors", data: [] },
      { name: "Drivers", data: [] },
    ],
    options: {
      chart: { type: "area", toolbar: { show: false }, stacked: false },
      stroke: { curve: "smooth", width: 2 },
      dataLabels: { enabled: false },
      colors: ["#589b3b", "#eb6718", "#3b82f6"],
      xaxis: { categories: [] },
      title: { text: "Registrations Overview" },
      legend: { position: "top" },
    },
  });

  // Order Trend (Bar)
  const [orderTrendChart, setOrderTrendChart] = useState({
    series: [{ name: "Orders", data: [] }],
    options: {
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 6 } },
      colors: ["#589b3b"],
      xaxis: { categories: [] },
      title: { text: "Monthly Orders" },
    },
  });

  // Donut Charts
  const [storeChart, setStoreChart] = useState({
    series: [0, 0, 0],
    options: {
      labels: ["Approved", "Rejected", "Pending"],
      colors: ["#589b3b", "#eb6718", "#ffc83d"],
      legend: { position: "bottom" },
      title: { text: "Store Status" },
    },
  });

  const [driverChart, setDriverChart] = useState({
    series: [0, 0, 0],
    options: {
      labels: ["Approved", "Rejected", "Pending"],
      colors: ["#589b3b", "#eb6718", "#ffc83d"],
      legend: { position: "bottom" },
      title: { text: "Driver Status" },
    },
  });

  const [orderStatusChart, setOrderStatusChart] = useState({
    series: [0, 0, 0],
    options: {
      labels: ["Completed", "Pending", "Cancelled"],
      colors: ["#589b3b", "#ffc83d", "#eb6718"],
      legend: { position: "bottom" },
      title: { text: "Order Status" },
    },
  });

  // ---------- Fetch Data ----------
  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    try {
      setLoading(true);
      const response = await dashboard();
      const data = response.data;
      setDetails(data);

      // --- Update Registrations Chart ---
      const months =
        data?.monthlyCustomerRegistrations?.map((item) => item.month) || [];
      setRegistrationsChart((prev) => ({
        ...prev,
        series: [
          {
            name: "Customers",
            data: data?.monthlyCustomerRegistrations?.map((i) => i.count) || [],
          },
          {
            name: "Vendors",
            data: data?.monthlyVendorRegistrations?.map((i) => i.count) || [],
          },
          {
            name: "Drivers",
            data: data?.monthlyDeliveryRegistrations?.map((i) => i.count) || [],
          },
        ],
        options: { ...prev.options, xaxis: { categories: months } },
      }));

      // --- Update Order Trend ---
      setOrderTrendChart((prev) => ({
        ...prev,
        series: [
          {
            name: "Orders",
            data: data?.monthlyOrderStatusAnalysis?.map((i) => i.count) || [],
          },
        ],
        options: { ...prev.options, xaxis: { categories: months } },
      }));

      // --- Update Donuts ---
      const vendorCount = data?.vendorCount?.[0] || {};
      setStoreChart((prev) => ({
        ...prev,
        series: [
          Number(vendorCount.approved_count || 0),
          Number(vendorCount.Rejected_count || 0),
          Number(vendorCount.not_approved_count || 0),
        ],
      }));

      const driverCount = data?.deliverpersonCount?.[0] || {};
      setDriverChart((prev) => ({
        ...prev,
        series: [
          Number(driverCount.approved_count || 0),
          Number(driverCount.Rejected_count || 0),
          Number(driverCount.not_approved_count || 0),
        ],
      }));

      setOrderStatusChart((prev) => ({
        ...prev,
        series: [
          Number(data?.completedOrders || 0),
          Number(data?.pendingOrders || 0),
          Number(data?.cancelledOrders || 0),
        ],
      }));

      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
      if (error?.status === 401) {
        localStorage.clear();
        navigate("/");
      }
    }
  };

  // ---------- Render ----------
  if (loading) return <Loader />;
  if (!details) return <div>No data</div>;

  const { realTimeOverview = {} } = details;
  const {
    orders = {},
    revenue = {},
    activeCounts = {},
    pendingIssues = {},
    quickStats = {},
  } = realTimeOverview;

  return (
    <>
      {/* =========================
          TOP 4 KPI CARDS
      ========================= */}
      <div className="kpi_grid">
        <div className="kpi_card">
          <div className="kpi_icon" style={{ background: "#589b3b" }}>
            <GroupIcon />
          </div>
          <div className="kpi_info">
            <span className="kpi_label">Total Users</span>
            <span className="kpi_value">{details.customerCount || 0}</span>
          </div>
        </div>
        <div className="kpi_card">
          <div className="kpi_icon" style={{ background: "#eb6718" }}>
            <AttachMoneyIcon />
          </div>
          <div className="kpi_info">
            <span className="kpi_label">Total Revenue</span>
            <span className="kpi_value">
              ${details.totalEarning?.toFixed(2) || 0}
            </span>
          </div>
        </div>
        <div className="kpi_card">
          <div className="kpi_icon" style={{ background: "#3b82f6" }}>
            <Reorder />
          </div>
          <div className="kpi_info">
            <span className="kpi_label">Total Orders</span>
            <span className="kpi_value">{details.orderCount || 0}</span>
          </div>
        </div>
        <div className="kpi_card">
          <div className="kpi_icon" style={{ background: "#8b5cf6" }}>
            <Category />
          </div>
          <div className="kpi_info">
            <span className="kpi_label">Categories</span>
            <span className="kpi_value">{details.categoryCount || 0}</span>
          </div>
        </div>
      </div>

      {/* =========================
          REAL-TIME OVERVIEW
      ========================= */}
      <div className="real_time_grid">
        <div className="rt_card">
          <h3>Orders</h3>
          <div className="rt_stats">
            <div>
              <span>Today</span> <strong>{orders?.today || 0}</strong>
            </div>
            <div>
              <span>This Week</span> <strong>{orders.thisWeek || 0}</strong>
            </div>
            <div>
              <span>This Month</span> <strong>{orders.thisMonth || 0}</strong>
            </div>
          </div>
        </div>
        <div className="rt_card">
          <h3>Revenue</h3>
          <div className="rt_stats">
            <div>
              <span>Today</span> <strong>${revenue?.today?.gross || 0}</strong>
            </div>
            <div>
              <span>This Week</span>{" "}
              <strong>${revenue?.thisWeek?.gross || 0}</strong>
            </div>
            <div>
              <span>This Month</span>{" "}
              <strong>${revenue?.thisMonth?.gross || 0}</strong>
            </div>
          </div>
        </div>
        <div className="rt_card">
          <h3>Active Now</h3>
          <div className="rt_stats">
            <div>
              <span>Users</span> <strong>{activeCounts?.users || 0}</strong>
            </div>
            <div>
              <span>Drivers</span>{" "}
              <strong>{activeCounts?.driversOnline || 0}</strong>
            </div>
            <div>
              <span>Stores</span>{" "}
              <strong>{activeCounts?.openStores || 0}</strong>
            </div>
          </div>
        </div>
      </div>

      {/* =========================
          CHARTS
      ========================= */}
      <div className="charts_grid">
        <div className="chart_card wide">
          <ReactApexChart
            options={registrationsChart?.options}
            series={registrationsChart?.series}
            type="area"
            height={300}
          />
        </div>
        <div className="chart_card">
          <ReactApexChart
            options={orderTrendChart?.options}
            series={orderTrendChart?.series}
            type="bar"
            height={300}
          />
        </div>
      </div>

      {/* =========================
          DONUT CHARTS
      ========================= */}
      <div className="donut_grid">
        <div className="donut_card">
          <ReactApexChart
            options={storeChart?.options}
            series={storeChart?.series}
            type="donut"
            height={280}
          />
        </div>
        <div className="donut_card">
          <ReactApexChart
            options={driverChart?.options}
            series={driverChart?.series}
            type="donut"
            height={280}
          />
        </div>
        <div className="donut_card">
          <ReactApexChart
            options={orderStatusChart?.options}
            series={orderStatusChart?.series}
            type="donut"
            height={280}
          />
        </div>
      </div>

      {/* =========================
          TOP PERFORMERS
      ========================= */}
      <div className="performers_grid">
        <div className="performer_card">
          <h3>
            <EmojiEventsIcon /> Top Vendors
          </h3>
          {details.topVendors?.slice(0, 5).map((v, i) => (
            <div className="performer_row" key={i}>
              <span>{v.shopName}</span>
              <strong>${v.totalSales}</strong>
            </div>
          ))}
        </div>
        <div className="performer_card">
          <h3>
            <WorkspacePremiumIcon /> Top Drivers
          </h3>
          {realTimeOverview.topDrivers?.slice(0, 5).map((d, i) => (
            <div className="performer_row" key={i}>
              <span>{d.fullName}</span>
              <strong>{d.completedDeliveries} deliveries</strong>
            </div>
          ))}
        </div>
      </div>

      {/* =========================
          PENDING ISSUES & QUICK STATS
      ========================= */}
      <div className="stats_grid">
        <div className="stat_card">
          <span className="stat_label">Support Tickets</span>
          <span className="stat_value">
            {pendingIssues.unresolvedSupportTickets || 0}
          </span>
        </div>
        <div className="stat_card">
          <span className="stat_label">Failed Orders (Today)</span>
          <span className="stat_value">
            {pendingIssues.failedOrdersToday || 0}
          </span>
        </div>
        <div className="stat_card">
          <span className="stat_label">Driver Approvals Pending</span>
          <span className="stat_value">
            {pendingIssues.pendingDriverApprovals || 0}
          </span>
        </div>
        <div className="stat_card">
          <span className="stat_label">Vendor Approvals Pending</span>
          <span className="stat_value">
            {pendingIssues.pendingVendorApprovals || 0}
          </span>
        </div>
        <div className="stat_card">
          <span className="stat_label">Avg. Delivery Time</span>
          <span className="stat_value">
            {quickStats.averageDeliveryTimeMinutes || 0} min
          </span>
        </div>
        <div className="stat_card">
          <span className="stat_label">Cancellation Rate</span>
          <span className="stat_value">
            {quickStats.cancellationRate || 0}%
          </span>
        </div>
      </div>

      {/* =========================
          ALERTS
      ========================= */}
      <div className="alerts_section">
        <h3>System Alerts</h3>
        <div className="alerts_list">
          {realTimeOverview.alerts?.length ? (
            realTimeOverview.alerts.map((alert, i) => (
              <div key={i} className={`alert_item ${alert.severity}`}>
                <strong>{alert.title}</strong>
                <span>{alert.message}</span>
              </div>
            ))
          ) : (
            <div className="alert_item info">All systems operational</div>
          )}
        </div>
      </div>

      {/* =========================
          LIVE ORDER TRACKING (simplified)
      ========================= */}
      <div className="live_section">
        <h3>
          <PlaceIcon /> Live Order Tracking
        </h3>
        <div className="live_stats">
          <span>
            Active Deliveries:{" "}
            <strong>{realTimeOverview.liveOrderMap?.length || 0}</strong>
          </span>
          <span className="badge">Map integration ready</span>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
